import { SchedulerConfig } from './schedulerConfig';

export type Navigation = {
  header: NavigationMenu;
  footer: NavigationMenu;
  /**
   * Per-tenant toggle/config for the three configurable footer blocks (Sponsors,
   * Social, Newsletter). Optional/absent — and any subset of its keys may be
   * missing — because a tenant that never opened the footer config screen has no
   * document for it at all. Always read this via the API's `resolveFooterComponents`
   * (or the frontend's mirrored fallback), never directly, so a missing/partial/
   * malformed value resolves to "all enabled with current default props" instead of
   * an empty or broken footer.
   */
  footerComponents?: FooterComponents;
};

export const MegaComponentType = {
  LeagueTable: 'leagueTable',
  Fixtures: 'fixtures',
  Results: 'results',
  TopScorers: 'topScorers',
} as const;
export type MegaComponentType = (typeof MegaComponentType)[keyof typeof MegaComponentType];

/** A single link inside a mega-menu links column. Unlike NavigationNode, mega links have no children. */
export type MegaLink = {
  id: string;
  label: string;
  url: string | null;
  sameTab: boolean;
  visible?: boolean;
};

export type MegaLinksColumn = {
  type: 'links';
  title: string;
  links: MegaLink[];
};

export type MegaComponentColumn = {
  type: 'component';
  component: MegaComponentType;
  /** Overrides which competition the embed shows; tenant default when absent. */
  competitionId?: string;
  /**
   * Full CMS config for the embedded component (the same form data the page
   * builder stores). When present the mega column renders the real CMS
   * component with this config; when absent it falls back to the compact
   * embed driven only by competitionId.
   */
  config?: Record<string, unknown>;
};

export type MegaColumn = MegaLinksColumn | MegaComponentColumn;

/** Desktop mega menu configuration — only meaningful on top-level header desktop nodes. 1–4 columns. */
export type MegaMenuConfig = {
  columns: MegaColumn[];
};

export type NavigationNode = {
  id: string;
  label: string;
  url: string | null;
  sameTab: boolean;
  visible?: boolean;
  /** Hex text color applied to the rendered nav label, e.g. "#E4002B". Absent = default theme color. */
  color?: string | null;
  /** Reused from page/component scheduling (packages/types/src/schedulerConfig.ts). Absent = always visible. */
  scheduler?: SchedulerConfig;
  children: NavigationNode[];
  mega?: MegaMenuConfig;
};

export type NavigationMenu = {
  desktopNodes: NavigationNode[];
  mobileNodes: NavigationNode[];
};

/**
 * The three footer blocks that can be toggled/configured (SC-1117). Values match the
 * corresponding `packages/ui` `CmsComponent` string literals 1:1 so frontend code can key
 * off `CmsComponent.SponsorsGrid` etc. directly — this package stays decoupled from
 * `packages/ui` (a frontend-only, MUI-carrying package) by re-declaring just the three
 * values it needs instead of importing `CmsComponent`.
 */
export const FooterComponentType = {
  SponsorsGrid: 'SponsorsGrid',
  NewsletterSubscribe: 'NewsletterSubscribe',
  SocialNetworks: 'SocialNetworks',
} as const;
export type FooterComponentType = (typeof FooterComponentType)[keyof typeof FooterComponentType];

/**
 * Per-component footer entry: a soft-hide toggle plus its config, reusing the CMS
 * component's own config-form shape (e.g. `SponsorsGridFormData`). `config` is always
 * fully replaced on update, never deep-merged server-side — see NavigationsService.
 */
export type FooterComponentConfig = {
  enabled: boolean;
  config: Record<string, unknown>;
};

/**
 * Stored per-tenant footer component configuration, keyed by `FooterComponentType`.
 * Any subset of keys may be present or absent (or the whole value malformed) — always
 * resolve through `resolveFooterComponents` (API) rather than reading this raw.
 */
export type FooterComponents = Partial<Record<FooterComponentType, FooterComponentConfig>>;
